import React, { useState } from "react";
import { uploadApi } from "../../../services/api/uploadApi";
import { postApi } from "../../../services/api/postApi";
import { FaImage, FaTimes } from "react-icons/fa";
import "./PostForms.css";

const PhotoPostForm = ({ onSuccess, onCancel }) => {
  const [content, setContent] = useState("");
  const [photos, setPhotos] = useState([]);
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [posting, setPosting] = useState(false);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);

    if (files.length + photos.length > 5) {
      alert("Maximum 5 photos allowed");
      return;
    }

    // Validate file types
    const invalidFiles = files.filter(
      (file) => !file.type.startsWith("image/")
    );
    if (invalidFiles.length > 0) {
      alert("Only image files are allowed");
      return;
    }

    // Validate file sizes (max 10MB per file)
    const largeFiles = files.filter((file) => file.size > 10 * 1024 * 1024);
    if (largeFiles.length > 0) {
      alert("Each image must be less than 10MB");
      return;
    }

    setUploading(true);

    try {
      // Upload all files in parallel
      const uploadPromises = files.map(async (file) => {
        const result = await uploadApi.uploadPhoto(file, (progress) => {
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: progress,
          }));
        });

        return {
          key: result.key,
          caption: "",
          preview: URL.createObjectURL(file),
        };
      });

      const uploadedPhotos = await Promise.all(uploadPromises);
      setPhotos([...photos, ...uploadedPhotos]);
    } catch (error) {
      alert("Photo upload failed");
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  // Update photo caption
  const updateCaption = (index, caption) => {
    const updated = [...photos];
    updated[index].caption = caption;
    setPhotos(updated);
  };

  // Remove photo
  const removePhoto = (index) => {
    const updated = photos.filter((_, i) => i !== index);
    setPhotos(updated);
  };

  // Submit post
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (photos.length === 0) {
      alert("Please add at least one photo");
      return;
    }

    setPosting(true);
    try {
      const response = await postApi.createPost({
        postType: "photo",
        content: content.trim(),
        photos: photos.map((p) => ({ key: p.key, caption: p.caption })),
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        visibility: visibility,
      });

      onSuccess(response.data.post);
    } catch (error) {
      alert("Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="post-form photo-post-form">
      <div className="form-group">
        <label htmlFor="content">Caption (optional)</label>
        <textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Say something about these photos..."
          rows={3}
          maxLength={2000}
        />
      </div>

      {/* Photo Upload */}
      <div className="photo-upload-section">
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          disabled={uploading || photos.length >= 5}
          id="photo-input"
          style={{ display: "none" }}
        />
        <label
          htmlFor="photo-input"
          className={`upload-label ${
            uploading || photos.length >= 5 ? "disabled" : ""
          }`}
        >
          <FaImage />
          {uploading ? "Uploading..." : `Add Photos (${photos.length}/5)`}
        </label>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="upload-progress-list">
          {Object.entries(uploadProgress).map(([name, progress]) => (
            <div key={name} className="progress-item">
              <span className="progress-filename">{name}</span>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="progress-percent">{progress}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Photo Previews */}
      {photos.length > 0 && (
        <div className="photo-previews">
          {photos.map((photo, index) => (
            <div key={index} className="photo-preview-card">
              <img src={photo.preview} alt={`Photo ${index + 1}`} />
              <button
                type="button"
                className="remove-photo-btn"
                onClick={() => removePhoto(index)}
              >
                <FaTimes />
              </button>
              <input
                type="text"
                placeholder="Add caption..."
                value={photo.caption}
                onChange={(e) => updateCaption(index, e.target.value)}
                className="photo-caption-input"
              />
            </div>
          ))}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="tags">Tags (optional)</label>
        <input
          type="text"
          id="tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="biology, fieldwork, research (comma-separated)"
        />
      </div>

      <div className="form-group">
        <label htmlFor="visibility">Visibility</label>
        <select
          id="visibility"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
        >
          <option value="public">Public</option>
          <option value="connections">Connections Only</option>
          <option value="private">Private</option>
        </select>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
        <button
          type="submit"
          disabled={uploading || posting || photos.length === 0}
          className="submit-btn"
        >
          {posting ? "Posting..." : "Post"}
        </button>
      </div>
    </form>
  );
};

export default PhotoPostForm;
